/**
 * Fill probability models for NBA moneyline orders.
 *
 * Two logistic regression models (L1/L2) trained on 7 days of Kalshi NBA
 * pre-game orderbook data estimate P(fill before tipoff) for resting YES
 * limit orders on KXNBAGAME-* markets.
 *
 * L1 = order at best bid, L2 = order at best bid - 1¢.
 */

// ── Types ──────────────────────────────────────────────────────────────

export type FillBand = 'high' | 'medium' | 'low' | 'very-low';
export type PriceLevel = 'L1' | 'L2';

export interface FillProbInput {
  hoursToTipoff: number;
  contractsAhead: number;
  fillVel30m: number;
}

export interface FillProbResult {
  level: PriceLevel;
  probability: number;
  band: FillBand;
  logit: number;
  inputs: FillProbInput;
}

// ── Coefficients ───────────────────────────────────────────────────────

export const L1_COEFFICIENTS = {
  intercept: -3.98830506,
  hoursToTipoff: 0.38942316,
  contractsAhead: -0.00000675,
  fillVel30m: 0.00004,
} as const;

export const L2_COEFFICIENTS = {
  intercept: -3.81549942,
  hoursToTipoff: 0.14679119,
  contractsAhead: -0.00000026,
  fillVel30m: 0.00005325,
} as const;

// ── Pure Functions ─────────────────────────────────────────────────────

export function sigmoid(logit: number): number {
  return 1 / (1 + Math.exp(-logit));
}

export function isNbaMoneylineTicker(ticker: string): boolean {
  return ticker.startsWith('KXNBAGAME');
}

export function isWithinModelRange(hours: number): boolean {
  return hours >= 1.0 && hours <= 12.5;
}

export function classifyFillBand(probability: number): FillBand {
  if (probability >= 0.8) return 'high';
  if (probability >= 0.5) return 'medium';
  if (probability >= 0.2) return 'low';
  return 'very-low';
}

/**
 * Determine price level for an order relative to best bid.
 * L1 = at best bid, L2 = best bid - 1¢. Returns null for deeper levels.
 */
export function determinePriceLevel(
  orderPriceCents: number,
  bestBidCents: number | null
): PriceLevel | null {
  if (bestBidCents == null) return null;
  if (orderPriceCents === bestBidCents) return 'L1';
  if (orderPriceCents === bestBidCents - 1) return 'L2';
  return null;
}

/**
 * Compute fill probability using logistic regression.
 * Returns null if hours are outside the trained range [1.0, 12.5].
 */
export function computeFillProbability(
  level: PriceLevel,
  input: FillProbInput
): FillProbResult | null {
  if (!isWithinModelRange(input.hoursToTipoff)) return null;

  const c = level === 'L1' ? L1_COEFFICIENTS : L2_COEFFICIENTS;
  const logit =
    c.intercept +
    c.hoursToTipoff * input.hoursToTipoff +
    c.contractsAhead * input.contractsAhead +
    c.fillVel30m * input.fillVel30m;

  const probability = sigmoid(logit);

  return {
    level,
    probability,
    band: classifyFillBand(probability),
    logit,
    inputs: input,
  };
}

/**
 * Estimated hours until fill via simple linear extrapolation.
 * Returns Infinity if fill velocity is zero.
 */
export function estHoursToFill(contractsAhead: number, fillVel30m: number): number {
  if (fillVel30m <= 0) return Infinity;
  const hourlyRate = fillVel30m * 2; // 30m window → 1hr rate
  return contractsAhead / hourlyRate;
}

/**
 * Extract event ticker from a market ticker by stripping the team suffix.
 * e.g. "KXNBAGAME-26MAR06DALBOS-BOS" → "KXNBAGAME-26MAR06DALBOS"
 */
export function extractEventTicker(marketTicker: string): string {
  const parts = marketTicker.split('-');
  if (parts.length >= 3) return parts.slice(0, 2).join('-');
  return marketTicker;
}
